import { z } from 'zod'

import { httpClientBanking } from '~/domains/banking/shared/infra/http-client-banking'
import { useQueryRequest } from '~/ui/hooks/utils'

import { ExtendedBalanceSchema } from '../..'

const MarketInvestedProductEarlyRedemptionSchema = z.object({
  available: z.boolean(),
  date: z.string().nullable()
})

export type MarketInvestedProductEarlyRedemption = z.infer<typeof MarketInvestedProductEarlyRedemptionSchema>

export const MarketInvestedProductSchema = z.object({
  apy: z.string(),
  balance: ExtendedBalanceSchema,
  earlyRedemption: MarketInvestedProductEarlyRedemptionSchema,
  firstInvestDate: z.string(),
  id: z.string(),
  maturityDate: z.string(),
  name: z.string()
})

export type MarketInvestedProduct = z.infer<typeof MarketInvestedProductSchema>

export const MarketInvestedProductsResponseSchema = z.object({
  products: z.array(MarketInvestedProductSchema).optional().default([])
})

export type MarketInvestedProductsResponse = z.infer<typeof MarketInvestedProductsResponseSchema>

async function fetchMarketInvestedProducts({ accountId }: { accountId: string }): Promise<MarketInvestedProduct[]> {
  const { data } = await httpClientBanking({
    url: `/investments/accounts/${accountId}/storefront/invested_products`,
    method: 'GET'
  })

  return MarketInvestedProductsResponseSchema.parse(data).products
}

export function getUseFetchMarketInvestedProductsKey({ accountId }: { accountId: string }) {
  return ['useFetchMarketInvestedProducts', accountId] as const
}

export function useFetchMarketInvestedProducts({
  accountId,
  options
}: {
  accountId: string
  options?: { suspense?: boolean }
}) {
  return useQueryRequest<Awaited<ReturnType<typeof fetchMarketInvestedProducts>>, unknown, true>(
    [...getUseFetchMarketInvestedProductsKey({ accountId })],
    () => fetchMarketInvestedProducts({ accountId }),
    {
      suspense: true,
      ...options
    }
  )
}
